import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import ProductCard from '../../components/ProductCard/ProductCard';
import useDebounce from '../../hooks/useDebounce';
import Banner from '../../components/Banner/Banner';
import AddToCartModal from '../../components/AddToCartModal/AddToCartModal';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import './Homepage.css';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return isMobile;
};

const Homepage = () => {
    const [barangList, setBarangList] = useState([]);
    const [jenisList, setJenisList] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedJenis, setSelectedJenis] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 500);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const isMobile = useIsMobile();
    const { addToCart } = useCart();
    const API_URL = api.defaults.baseURL.replace('/api', '');

    const observer = useRef();
    const lastBarangElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        const fetchBarang = async () => {
            setLoading(true);
            try {
                const { data } = await api.get('/barang/customer', {
                    params: { page, limit: 20, search: debouncedSearch, jenisId: selectedJenis || undefined }
                });
                const newBarang = data.data || [];
                setBarangList(prev => (page === 1 ? newBarang : [...prev, ...newBarang]));
                setHasMore(data.currentPage < data.totalPages);
            } catch (error) {
                console.error("Gagal mengambil produk:", error);
                toast.error("Gagal memuat produk.");
            } finally {
                setLoading(false);
            }
        };

        if (hasMore || page === 1) {
            fetchBarang();
        }
    // --- PERBAIKAN DI SINI: Tambahkan 'hasMore' ke dependency array ---
    }, [page, debouncedSearch, selectedJenis, hasMore]);

    useEffect(() => {
        // Efek ini akan me-reset daftar produk saat filter berubah
        setPage(1);
        setHasMore(true);
        setBarangList([]); 
    }, [debouncedSearch, selectedJenis]);

    useEffect(() => {
        const fetchJenis = async () => {
            try {
                const { data } = await api.get('/jenis?limit=1000');
                setJenisList(data.data || []);
            } catch (error) {
                console.error("Gagal mengambil jenis:", error);
            }
        };
        fetchJenis();
    }, []);

    const handleAddToCartClick = async (barang) => {
        const toastId = toast.loading("Menambahkan...");
        try {
            const { data: detailBarang } = await api.get(`/barang/customer/${barang.id}`);
            const defaultVariant = detailBarang.Products?.[0];

            if (!defaultVariant) {
                toast.update(toastId, { render: "Varian produk tidak tersedia.", type: "error", isLoading: false, autoClose: 3000 });
                return;
            }

            if (isMobile) {
                setSelectedProduct(detailBarang);
                setIsModalOpen(true);
                toast.dismiss(toastId);
            } else {
                await addToCart(defaultVariant.id, 1);
                toast.update(toastId, { render: `${detailBarang.nama_barang} (${defaultVariant.nama_satuan}) ditambahkan!`, type: "success", isLoading: false, autoClose: 2000 });
            }
        } catch (error) {
            const message = error.response?.data?.message || "Gagal menambahkan ke keranjang.";
            toast.update(toastId, { render: message, type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);
    };

    return (
        <>
            <div className="homepage-container">
                <Banner />
                <div className="product-section">
                    <h2 className="section-title">Semua Produk</h2>
                    <div className="filters-bar">
                        <input type="text" placeholder="Cari nama barang atau merek..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
                        <div className="category-scroll-container">
                            <button onClick={() => setSelectedJenis('')} className={`category-chip ${selectedJenis === '' ? 'active' : ''}`}>Semua Jenis</button>
                            {jenisList.map(j => (
                                <button key={j.id} onClick={() => setSelectedJenis(j.id)} className={`category-chip ${selectedJenis === j.id ? 'active' : ''}`}>{j.nama_jenis}</button>
                            ))}
                        </div>
                    </div>
                    <main className="product-grid">
                        {barangList.map((barang, index) => {
                            if (barangList.length === index + 1) {
                                return (
                                    <div ref={lastBarangElementRef} key={barang.id}>
                                        <ProductCard barang={barang} API_URL={API_URL} onAddToCartClick={handleAddToCartClick} />
                                    </div>
                                );
                            } else {
                                return <ProductCard key={barang.id} barang={barang} API_URL={API_URL} onAddToCartClick={handleAddToCartClick} />;
                            }
                        })}
                    </main>
                    {loading && <p className="loading-text">Memuat produk...</p>}
                    {!loading && !hasMore && barangList.length > 0 && <p className="loading-text">Semua produk sudah ditampilkan.</p>}
                    {!loading && barangList.length === 0 && <p className="loading-text">Produk tidak ditemukan.</p>}
                </div>
            </div>
            {isModalOpen && (<AddToCartModal barang={selectedProduct} onClose={handleCloseModal} />)}
        </>
    );
};

export default Homepage;